import React from 'react';

import styles from './UsedCoupon.module.scss';

/**
 * Grid card for an applied/reserved/used coupon — used by UsedCoupons'
 * CouponList (shared by HistoryCouponsList and ReservedCouponsList).
 * Restyled to match CustomerCouponCard's grid layout: image on top (1:1,
 * object-fit cover) with the status badge overlaid, then a body with
 * title/expiry and a footer row holding the status pill + cancel button.
 *
 * Props:
 *   data         — applied-coupon record: { id, state, expiry_time,
 *                  coupon_details: { title, image_url } }
 *   first        — true for the first tile in a list (kept for API parity
 *                  with the other list-tile components; no special styling
 *                  needed in a grid layout)
 *   onPress      — click handler on the image/title (e.g. navigate to the
 *                  reserved-coupon detail view)
 *   onCancel     — click handler for the cancel button
 *   enableButton — whether to show the cancel button at all (false for
 *                  history items that are already resolved, true for
 *                  active reservations)
 */
const UsedCoupon = ({ data, onPress, onCancel, enableButton }) => {
  const { state, expiry_time: expiryTime, coupon_details: couponDetails } = data || {};
  const { title, image_url: imageUrls } = couponDetails || {};
  const thumb = Array.isArray(imageUrls) ? imageUrls[0]?.image_url : imageUrls;

  const expiryText = expiryTime
    ? new Date(expiryTime).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    : null;

  return (
    <div className={styles.card}>
      <button type="button" className={styles.imageWrap} onClick={onPress}>
        <div className={styles.imageContainer}>
          {thumb ? (
            <img src={thumb} alt={title || ''} className={styles.thumb} />
          ) : (
            <div className={styles.noImagePlaceholder} />
          )}
        </div>

        {state && (
          <div className={styles.badgesRow}>
            <span className={`${styles.statusBadge} ${styles[state] || ''}`}>{state}</span>
          </div>
        )}
      </button>

      <div className={styles.body}>
        <button type="button" className={styles.footer} onClick={onPress}>
          <h3 className={styles.title}>{title || 'Coupon'}</h3>
          {expiryText && <p className={styles.meta}>Expires {expiryText}</p>}
        </button>

        {enableButton && onCancel && (
          <div className={styles.footerBottom}>
            <button type="button" className={styles.cancelBtn} onClick={onCancel}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsedCoupon;